import { useRef } from "react"
import { isSameDay, isAfter, isBefore, isToday } from "date-fns"
import cn from "@/v2-ui/utils/utils.cn"
import { useDateRangePickerContext } from "./dateRangePicker.context"

type DateRangePickerDayProps = {
  day: Date
}

function DateRangePickerDay(props: DateRangePickerDayProps) {
  const { day } = props
  const ctx = useDateRangePickerContext()
  const { mode, start, end, minDate, maxDate, activeEdge } = ctx

  const didDragRef = useRef(false)
  const lastDragDateRef = useRef("")
  const startPosRef = useRef({ x: 0, y: 0 })
  const ctxRef = useRef(ctx)
  ctxRef.current = ctx

  const outOfBounds =
    (minDate && isBefore(day, minDate)) || (maxDate && isAfter(day, maxDate))

  const isStart = start && isSameDay(day, start)
  const isEnd = end && isSameDay(day, end)
  const selected =
    mode === "within" ? undefined : mode === "before" ? end : start
  const isSelectedDay =
    isStart || isEnd || (selected && isSameDay(day, selected))
  const inRange = start && end && isAfter(day, start) && isBefore(day, end)
  const today = isToday(day)
  const rangeStart = !!(isStart && end)
  const rangeEnd = !!(isEnd && start)
  const isDraggable = (rangeStart || rangeEnd) && !outOfBounds

  function handleClick() {
    if (didDragRef.current) {
      didDragRef.current = false
      return
    }

    const { selectRange, selectSingle, setActiveEdge } = ctx

    if (mode !== "within") {
      selectSingle(day)
      return
    }

    if (activeEdge === "start") {
      if (end && isAfter(day, end)) {
        selectRange({ start: end, end: day })
        setActiveEdge("end")
      } else {
        selectRange({ start: day, end })
        setActiveEdge("end")
      }
      return
    }

    if (activeEdge === "end") {
      if (start && isBefore(day, start)) {
        selectRange({ start: day, end: start })
        setActiveEdge("start")
      } else {
        selectRange({ start, end: day })
        setActiveEdge("start")
      }
      return
    }

    if (!start || (start && end)) {
      selectRange({ start: day, end: undefined })
      setActiveEdge("end")
    } else if (isSameDay(day, start)) {
      selectRange({ start: day, end: day })
    } else if (isBefore(day, start)) {
      selectRange({ start: day, end: start })
    } else {
      selectRange({ start, end: day })
    }
  }

  function handlePointerDown(e: React.PointerEvent) {
    const edge = rangeStart ? "start" : "end"
    didDragRef.current = false
    lastDragDateRef.current = ""
    startPosRef.current = { x: e.clientX, y: e.clientY }
    ctxRef.current.setActiveEdge(edge)

    function onMove(ev: PointerEvent) {
      if (!didDragRef.current) {
        const dx = ev.clientX - startPosRef.current.x
        const dy = ev.clientY - startPosRef.current.y
        if (Math.abs(dx) < 3 && Math.abs(dy) < 3) return
        didDragRef.current = true
      }

      const el = document.elementFromPoint(ev.clientX, ev.clientY)
      const dayEl = el?.closest("[data-date]") as HTMLElement | null
      if (!dayEl) return
      const dateStr = dayEl.dataset.date
      if (!dateStr || dateStr === lastDragDateRef.current) return
      lastDragDateRef.current = dateStr

      const c = ctxRef.current
      const dragDate = new Date(dateStr)
      if (c.activeEdge === "start") {
        if (c.end && isAfter(dragDate, c.end)) {
          c.selectRange({ start: c.end, end: dragDate })
          c.setActiveEdge("end")
        } else {
          c.selectRange({ start: dragDate, end: c.end })
        }
      } else if (c.activeEdge === "end") {
        if (c.start && isBefore(dragDate, c.start)) {
          c.selectRange({ start: dragDate, end: c.start })
          c.setActiveEdge("start")
        } else {
          c.selectRange({ start: c.start, end: dragDate })
        }
      }
    }

    function onUp() {
      document.removeEventListener("pointermove", onMove)
      document.removeEventListener("pointerup", onUp)
    }

    document.addEventListener("pointermove", onMove)
    document.addEventListener("pointerup", onUp)
  }

  return (
    <button
      type="button"
      data-date={day.toISOString()}
      disabled={!!outOfBounds}
      className={cn(
        "ui-date-picker-day",
        outOfBounds && "ui-date-picker-day-disabled",
        today && "ui-date-picker-day-today",
        isSelectedDay && !outOfBounds && "ui-date-picker-day-selected",
        rangeStart && !outOfBounds && "ui-date-picker-day-range-start",
        rangeEnd && !outOfBounds && "ui-date-picker-day-range-end",
        inRange && !outOfBounds && "ui-date-picker-day-range",
        isDraggable && "ui-date-picker-day-draggable",
      )}
      onClick={handleClick}
      onPointerDown={isDraggable ? handlePointerDown : undefined}
    >
      {day.getDate()}
      {rangeStart && <span className="ui-date-picker-day-label">start</span>}
      {rangeEnd && <span className="ui-date-picker-day-label">end</span>}
    </button>
  )
}

export default DateRangePickerDay
